import React, { cloneElement, isValidElement, useMemo } from 'react';
import { ElementQuantityName } from '@shared/models/element-quantities.model';
import { EmissionCostLabel } from '@shared/models/unit.model';

import { Box, Theme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { CurrencyBaseUnit } from '@shared/models/units/currency-units.model';
import { guards } from '@shared/helpers/typeguards.helpers';

import CO2Icon from './icons/cloud.svg?react';
import CostSEKIcon from './icons/sek.svg?react';
import FXIcon from './icons/fx.svg?react';
import FilterIcon from './icons/filter.svg?react';
import ProjectListViewIcon from './icons/project-list-view.svg?react';
import ProjectMapViewIcon from './icons/project-map-view.svg?react';
import ToggleOnIcon from './icons/toggle-on.svg?react';
import ToggleOffIcon from './icons/toggle-off.svg?react';
import ActionIcon from './icons/action.svg?react';
import ArrowDownRightIcon from './icons/arrow-down-right.svg?react';
import ArrowTrendUpIcon from './icons/arrow-trend-up.svg?react';
import ArrowTrendDownIcon from './icons/arrow-trend-down.svg?react';
import DownloadIcon from './icons/download.svg?react';
import ShareIcon from './icons/share.svg?react';
import BuildingOutlineIcon from './icons/building-outline.svg?react';
import GenericCarbonConservativeIcon from './icons/generic-carbon-conservative.svg?react';
import GenericCarbonTypicalIcon from './icons/generic-carbon-typical.svg?react';
import {
  CanvasDrawIcon,
  CanvasMeasureIcon,
  CanvasMoveIcon,
  CanvasPanIcon,
  CanvasSelectIcon,
} from './icons/canvas-tools';
import { CommentIcon, CommentUnreadIcon } from './icons/comment';

import { QuantityIcons } from './icons/quantities';
import { CurrencyIcons } from './icons/currencies';
import { CategoryIcons } from './icons/categories';
import * as MuiIcons from '@mui/icons-material';
import { NodonTheme } from '@client/style';

export const uiIcons = {
  filter: <FilterIcon />,
  projectListView: <ProjectListViewIcon />,
  projectMapView: <ProjectMapViewIcon />,
  fx: <FXIcon />,
  toggleOn: <ToggleOnIcon />,
  toggleOff: <ToggleOffIcon />,
  action: <ActionIcon />,
  arrowDownRight: <ArrowDownRightIcon />,
  arrowTrendUp: <ArrowTrendUpIcon />,
  arrowTrendDown: <ArrowTrendDownIcon />,
  download: <DownloadIcon />,
  share: <ShareIcon />,
  buildingOutline: <BuildingOutlineIcon />,
  genericCarbonConservative: <GenericCarbonConservativeIcon />,
  genericCarbonTypical: <GenericCarbonTypicalIcon />,
  categorySquare: (
    <MuiIcons.CropSquareSharp sx={{ transform: 'rotate(45deg) scale(0.5)' }} />
  ),
  categoryProduct: <MuiIcons.CircleOutlined sx={{ transform: 'scale(0.5)' }} />,
  /** Framer-like Design / import canvas toolbar icons (stroke). */
  canvasSelect: <CanvasSelectIcon />,
  canvasPan: <CanvasPanIcon />,
  canvasDraw: <CanvasDrawIcon />,
  canvasMove: <CanvasMoveIcon />,
  canvasMeasure: <CanvasMeasureIcon />,
  comment: <CommentIcon />,
  commentUnread: <CommentUnreadIcon />,
} satisfies Record<string, React.ReactNode>;
export const muiIcons = {
  Add: <MuiIcons.Add />,
  AddCircle: <MuiIcons.AddCircle />,
  AddCircleOutline: <MuiIcons.AddCircleOutlined />,
  Replay: <MuiIcons.Replay />,
  Eject: <MuiIcons.Eject />,
  Undo: <MuiIcons.Undo />,
  Redo: <MuiIcons.Redo />,
  Save: <MuiIcons.Save />,
  SaveAlt: <MuiIcons.SaveAlt />,
  ArrowLeft: <MuiIcons.ArrowLeft />,
  ArrowRight: <MuiIcons.ArrowRight />,
  ArrowForward: <MuiIcons.ArrowForward />,
  ArrowDownward: <MuiIcons.ArrowDownward />,
  ArrowUpward: <MuiIcons.ArrowUpward />,
  ArrowBack: <MuiIcons.ArrowBack />,
  ArrowDropDown: <MuiIcons.ArrowDropDown />,
  AutoAwesome: <MuiIcons.AutoAwesome />,
  Check: <MuiIcons.Check />,
  CheckCircleOutline: <MuiIcons.CheckCircleOutlined />,
  CircleOutlined: <MuiIcons.CircleOutlined />,
  Clear: <MuiIcons.Clear />,
  Close: <MuiIcons.Close />,
  Comment: <MuiIcons.Comment />,
  CreateNewFolder: <MuiIcons.CreateNewFolder />,
  Delete: <MuiIcons.Delete />,
  DeleteForever: <MuiIcons.DeleteForever />,
  DriveFileRenameOutline: <MuiIcons.DriveFileRenameOutline />,
  InsertDriveFileOutlined: <MuiIcons.InsertDriveFileOutlined />,
  Refresh: <MuiIcons.Refresh />,
  FileCopy: <MuiIcons.FileCopy />,
  Edit: <MuiIcons.Edit />,
  EditOutlined: <MuiIcons.EditOutlined />,
  Fullscreen: <MuiIcons.Fullscreen />,
  MoreVert: <MuiIcons.MoreVert />,
  MoreHoriz: <MuiIcons.MoreHoriz />,
  Search: <MuiIcons.Search />,
  VerticalAlignBottom: <MuiIcons.VerticalAlignBottom />,
  ExpandMore: <MuiIcons.ExpandMore />,
  ArrowDropDownIcon: <MuiIcons.ArrowDropDown />,
  Folder: <MuiIcons.Folder />,
  FolderOpen: <MuiIcons.FolderOpen />,
  LocalShipping: <MuiIcons.LocalShipping />,
  AttachMoney: <MuiIcons.AttachMoney />,
  KeyboardArrowUp: <MuiIcons.KeyboardArrowUp />,
  KeyboardArrowDown: <MuiIcons.KeyboardArrowDown />,
  KeyboardArrowLeft: <MuiIcons.KeyboardArrowLeft />,
  KeyboardArrowRight: <MuiIcons.KeyboardArrowRight />,
  LightbulbOutlined: <MuiIcons.LightbulbOutlined />,
  IosShare: <MuiIcons.IosShare />,
  InfoOutlined: <MuiIcons.InfoOutlined />,
  Visibility: <MuiIcons.Visibility />,
  VisibilityOff: <MuiIcons.VisibilityOff />,
  WarningAmber: <MuiIcons.WarningAmber />,
  Lock: <MuiIcons.Lock />,
  LockOpen: <MuiIcons.LockOpen />,
  Archive: <MuiIcons.Archive />,
  Unarchive: <MuiIcons.Unarchive />,
  ListAlt: <MuiIcons.ListAlt />,
  LabelOutlined: <MuiIcons.LabelOutlined />,
  BorderAll: <MuiIcons.BorderAll />,
  Upload: <MuiIcons.Upload />,
  CopyAll: <MuiIcons.CopyAll />,
  ContentCopy: <MuiIcons.ContentCopy />,
  CropSquare: <MuiIcons.CropSquare />,
  LibraryAddCheck: <MuiIcons.LibraryAddCheck />,
  SwapVert: <MuiIcons.SwapVert />,
  SwapHoriz: <MuiIcons.SwapHoriz />,
  Key: <MuiIcons.Key />,
  Settings: <MuiIcons.Settings />,
  PhotoCamera: <MuiIcons.PhotoCamera />,
  GetApp: <MuiIcons.GetApp />,
  PostAdd: <MuiIcons.PostAdd />,
  Remove: <MuiIcons.Remove />,
  RotateLeft: <MuiIcons.RotateLeft />,
  RotateRight: <MuiIcons.RotateRight />,
  Straighten: <MuiIcons.Straighten />,
  PanTool: <MuiIcons.PanTool />,
  OpenWith: <MuiIcons.OpenWith />,
  NearMe: <MuiIcons.NearMe />,
  Architecture: <MuiIcons.Architecture />,
  Pentagon: <MuiIcons.Pentagon />,
  ZoomIn: <MuiIcons.ZoomIn />,
  ZoomOut: <MuiIcons.ZoomOut />,
  CenterFocusStrong: <MuiIcons.CenterFocusStrong />,
} satisfies Record<string, React.ReactNode>;

const elementCategoryIcons = {
  beam: <CategoryIcons.Beam />,
  column: <CategoryIcons.Column />,
  cooling: <CategoryIcons.Cooling />,
  heating: <CategoryIcons.Heating />,
  labour: <CategoryIcons.Labour />,
  other: <CategoryIcons.Other />,
  slab: <CategoryIcons.Slab />,
  ventilation: <CategoryIcons.Ventilation />,
  wall: <CategoryIcons.Wall />,
  windows: <CategoryIcons.Windows />,
  ceramics: <CategoryIcons.Ceramics />,
  concrete: <CategoryIcons.Concrete />,
  energy: <CategoryIcons.Energy />,
  gypsum: <CategoryIcons.Gypsum />,
  insulation: <CategoryIcons.Insulation />,
  metal: <CategoryIcons.Metal />,
  otherproduct: <CategoryIcons.OtherProduct />,
  unknownproduct: <CategoryIcons.UnknownProduct />,
  wood: <CategoryIcons.Wood />,
  solar: <CategoryIcons.Solar />,
  maintenance: <MuiIcons.Build sx={{ transform: 'scale(0.7)' }} />, // TODO: make our own maintenance icon
} as const satisfies Record<string, React.ReactNode>;

export const costEmissionIcons: Record<EmissionCostLabel, React.ReactNode> = {
  kgCO2e: <CO2Icon />,
  /**
   * @deprecated use CurrencySEKIcon instead
   */
  SEK: <CostSEKIcon />,
  /**
   * @deprecated use SEK instead
   */
  kSEK: <CostSEKIcon />, // this is a SEK icon, not a kSEK icon
};

type QuantityIconName = Extract<
  ElementQuantityName,
  | 'length'
  | 'width'
  | 'height'
  | 'area_side'
  | 'area_top'
  | 'area_section'
  | 'volume'
  | 'fill'
  | 'mass'
>;

export const quantityIcons: Record<QuantityIconName, React.ReactNode> = {
  length: <QuantityIcons.Length />,
  width: <QuantityIcons.Width />,
  height: <QuantityIcons.Height />,
  area_side: <QuantityIcons.SideArea />,
  area_top: <QuantityIcons.TopArea />,
  area_section: <QuantityIcons.SectionArea />,
  volume: <QuantityIcons.Volume />,
  fill: <QuantityIcons.Fill />,
  mass: <QuantityIcons.Mass />,
};
export const currencyIcons: Record<CurrencyBaseUnit | 'NONE', React.ReactNode> =
  {
    SEK: <CurrencyIcons.SEK />,
    USD: <CurrencyIcons.USD />,
    EUR: <CurrencyIcons.EUR />,
    NONE: <CurrencyIcons.NONE />,
  };
export const isQuantityIconName = (icon: string): icon is QuantityIconName => {
  return icon in quantityIcons;
};

export const icons = {
  mui: muiIcons,
  ui: uiIcons,
  costEmission: costEmissionIcons,
  quantity: quantityIcons,
  elementCategory: elementCategoryIcons,
  currency: currencyIcons,
} as const;

type IconSize = 'small' | 'medium' | 'large';

const sizeMap: Record<string, number> = {
  small: 12,
  medium: 18,
  large: 24,
} satisfies Record<IconSize, number>;

const isIconSize = (size: unknown): size is IconSize =>
  guards.is.string(size) && size in sizeMap;

type Size = number | IconSize | NonCollapsibleString;

type MainColors = Extract<
  keyof Theme['palette'],
  'primary' | 'secondary' | 'warning' | 'error' | 'info' | 'success' | 'neutral'
>;
type IconColor = Pretty<
  MainColors | `text.${Pretty<keyof Theme['palette']['text']>}`
>;
export type NodonIcons = typeof icons;
export type IconCategory = keyof NodonIcons;
export type IconProps<Category extends IconCategory = IconCategory> = {
  category: Category;
  icon: keyof (typeof icons)[Category];
  /* size in pixels or one of the predefined sizes. *only use pixels in edge cases* */
  size?: Size;
  /* size of the outer container. Use if you need to align icons of different sizes */
  outerSize?: Size;
  /* rotate the icon in degrees */
  rotate?: number;
  /* color of the icon */
  color?: IconColor;
};

const resolveColor = (color?: IconColor): string | undefined => {
  if (!color) return undefined;
  if (color.startsWith('text.')) {
    const textColor = color.replace('text.', '');
    const textColors = NodonTheme.palette.text;
    return guards.is.keyOf(textColors, textColor)
      ? textColors[textColor]
      : undefined;
  }

  const paletteColor = guards.is.keyOf(NodonTheme.palette, color)
    ? NodonTheme.palette[color].main
    : undefined;

  return guards.is.string(paletteColor) ? paletteColor : undefined;
};

export const Icon = <Category extends IconCategory = IconCategory>({
  category,
  icon,
  size = 'medium',
  outerSize = size,
  color,
  rotate = 0,
}: IconProps<Category>) => {
  const resolvedSize = isIconSize(size) ? sizeMap[size] : size;
  const resolvedOuterSize = isIconSize(outerSize)
    ? sizeMap[outerSize]
    : outerSize;
  const resolvedColor = resolveColor(color);
  const { classes, cx } = useStyles({
    size: resolvedSize,
    color: resolvedColor,
  });
  const iconElement = icons[category][icon] as React.ReactNode;

  // Inject sizing/color/rotation straight onto the svg element instead of a
  // wrapper Box. cloneElement merges with each icon's baked props (e.g. the
  // `sx` scale on Build/CropSquareSharp) rather than replacing them, and it
  // creates no DOM node — removing one styled <div> per icon app-wide.
  const styledIcon = useMemo<React.ReactNode>(() => {
    if (!isValidElement(iconElement)) return iconElement;
    const el = iconElement as React.ReactElement<{
      className?: string;
      style?: React.CSSProperties;
    }>;
    return cloneElement(el, {
      className: cx(classes.icon, el.props.className),
      style: rotate
        ? {
            transform: `rotate(${rotate}deg)`,
            transition: 'transform 0.2s ease-in-out',
            ...el.props.style,
          }
        : el.props.style,
    });
  }, [iconElement, classes.icon, cx, rotate]);

  // Only a larger outer container (outerSize > size, used to align mixed icon
  // sizes) still needs the wrapper Box; the common equal-size case is flat.
  if (resolvedOuterSize === resolvedSize) return <>{styledIcon}</>;

  return (
    <Box
      className={classes.outer}
      sx={{
        width: resolvedOuterSize,
        height: resolvedOuterSize,
      }}
    >
      {styledIcon}
    </Box>
  );
};

const useStyles = makeStyles<{ size?: Size; color?: string }>()((
  _theme,
  { size, color },
) => {
  return {
    icon: {
      flexShrink: 0,
      display: 'block',
      width: size,
      height: size,
      color: color ?? 'inherit',
      fill: 'currentColor',
      '& path': {
        fill: 'currentColor',
      },
      // Stroke-only icons (no fill attribute / fill none).
      '& path[stroke][fill="none"]': {
        fill: 'none',
        stroke: 'currentColor',
      },
      // Canvas tools: light fill + outline via CSS variables on the button.
      '&.nodonCanvasToolIcon path': {
        fill: 'var(--tool-icon-fill, currentColor)',
      },
      '&.nodonCanvasToolIcon path[stroke]': {
        stroke: 'var(--tool-icon-outline, currentColor)',
      },
      '&.nodonCanvasToolIcon path[fill="none"]': {
        fill: 'none',
      },
    },
    outer: {
      display: 'flex',
      flexShrink: 0,
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
    },
  };
});
